/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHIFT_PREFS = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];
const CERTS = ['Food Handler Card', 'Alcohol Certification', 'Bar Certified', 'Manager Certified', 'First Aid'];

function formatTime(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const p = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${p}`;
}

export default function EmployeeView({ onManagerView, previewEmployeeId }) {
  const [activeTab, setActiveTab] = useState('schedule');
  const [requestType, setRequestType] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseShift, setReleaseShift] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseIncentive, setReleaseIncentive] = useState('');
  const [availableCoworkers, setAvailableCoworkers] = useState([]);
  const [aiRankings, setAiRankings] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [releaseStep, setReleaseStep] = useState(1);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [prefShifts, setPrefShifts] = useState([]);
  const [prefDays, setPrefDays] = useState([]);
  const [maxHours, setMaxHours] = useState(30);
  const [certs, setCerts] = useState([]);
  const [emergencyContact, setEmergencyContact] = useState('');
  const [employeeNotes, setEmployeeNotes] = useState('');
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [timeOffDates, setTimeOffDates] = useState([]);
  const [timeOffNote, setTimeOffNote] = useState('');
  const [timeOffMonth, setTimeOffMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [timeOffSubmitted, setTimeOffSubmitted] = useState(false);
  const [openShifts, setOpenShifts] = useState([]);
  const [myReleases, setMyReleases] = useState({});
  const [editIncentive, setEditIncentive] = useState(null);
  const [newIncentiveVal, setNewIncentiveVal] = useState('');
  const [pickupNote, setPickupNote] = useState('');
  const [pickupSubmitted, setPickupSubmitted] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatUnread, setChatUnread] = useState(0);
  const [teamShifts, setTeamShifts] = useState([]);
  const [teamRoleFilter, setTeamRoleFilter] = useState('my');
  const [teamWeekOffset, setTeamWeekOffset] = useState(0);
  const [swapTarget, setSwapTarget] = useState(null);
  const [swapMode, setSwapMode] = useState(null); // 'trade' | 'pickup'
  const [swapMyShift, setSwapMyShift] = useState(null);
  const [swapNote, setSwapNote] = useState('');
  const [swapSubmitting, setSwapSubmitting] = useState(false);
  const [swapError, setSwapError] = useState('');
  const [swapToast, setSwapToast] = useState('');
  const [allSwapRequests, setAllSwapRequests] = useState([]);
  const [incomingSwaps, setIncomingSwaps] = useState([]);
  const [outgoingSwaps, setOutgoingSwaps] = useState([]);
  const [swapBadge, setSwapBadge] = useState(0);

  useEffect(() => { fetchData(); }, []);

  // Poll for fresh open shifts data every 30s when on the open tab
  useEffect(() => {
    if (activeTab !== 'open') return;
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  async function fetchData() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: emps } = await supabase.from('employees').select('*');
    if (!emps) return;
    setEmployees(emps);
    const me = previewEmployeeId ? emps.find(e => e.id === previewEmployeeId) : emps.find(e => e.email === user.email);
    setMyEmployee(me);
    if (me && !me.profile_completed) setShowOnboarding(true);
    if (me) {
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      const dates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return d.toISOString().split('T')[0];
      });
      const { data: sh } = await supabase.from('shifts').select('*').eq('employee_id', me.id).in('day', dates);
      setShifts(sh || []);
      // Fetch my own releases from BOTH tables to show status on shift cards
      const { data: myRels } = await supabase.from('shift_releases').select('*').eq('employee_id', me.id).in('status', ['open', 'claimed']);
      const { data: myReqs } = await supabase.from('requests').select('*').eq('company_id', me.company_id).eq('employee_name', me.name).eq('type', 'shift_release').in('status', ['pending', 'approved']);
      const relMap = {};
      (myRels || []).forEach(r => { relMap[r.shift_id] = r; });
      // Also index by date+period for requests (since they don't have shift_id)
      (myReqs || []).forEach(r => {
        const dateMatch = r.detail?.match(/\d{4}-\d{2}-\d{2}/);
        const periodMatch = r.detail?.match(/Release (\w[\w\s]*?) on/);
        if (dateMatch && periodMatch) {
          // Find matching shift to get shift_id
          const matchShift = (sh || []).find(s => s.day === dateMatch[0] && s.shift_name === periodMatch[1]);
          if (matchShift && !relMap[matchShift.id]) {
            relMap[matchShift.id] = { ...r, incentive: 0, shift_id: matchShift.id, _fromRequests: true };
          }
        }
      });
      setMyReleases(relMap);
      // Fetch open shift releases, filtered to this employee's roles
      // NOTE: Run this SQL in Supabase if updated_at column doesn't exist:
      //   ALTER TABLE shift_releases ADD COLUMN IF NOT EXISTS updated_at timestamptz DEFAULT now();
      //   CREATE OR REPLACE FUNCTION update_updated_at() RETURNS TRIGGER AS $$ BEGIN NEW.updated_at = now(); RETURN NEW; END; $$ LANGUAGE plpgsql;
      //   DROP TRIGGER IF EXISTS shift_releases_updated_at ON shift_releases;
      //   CREATE TRIGGER shift_releases_updated_at BEFORE UPDATE ON shift_releases FOR EACH ROW EXECUTE FUNCTION update_updated_at();
      const myRoles = typeof me.role === 'string' ? me.role.split(',').map(r => r.trim()) : (me.role || []);
      // Only show releases that are still open (not claimed/covered)
      const { data: rels } = await supabase.from('shift_releases').select('*').eq('company_id', me.company_id).eq('status', 'open').order('created_at', { ascending: false });
      // Also get this employee's current shift dates to exclude already-covered shifts
      const myShiftKeys = new Set((sh || []).map(s => `${s.day}-${s.shift_name}-${s.role}`));
      const filtered = (rels || []).filter(r => {
        if (!myRoles.includes(r.role)) return false;
        if (String(r.employee_id) === String(me.id)) return false;
        if (r.covered_by && String(r.covered_by) === String(me.id)) return false;
        // Exclude if this employee already has a shift for this exact slot (they already picked it up)
        if (myShiftKeys.has(`${r.shift_date}-${r.shift_name}-${r.role}`)) return false;
        return true;
      });
      // Deduplicate by shift_id — keep the record with the highest incentive
      const seen = {};
      filtered.forEach(r => {
        const key = r.shift_id || r.id;
        if (!seen[key] || (r.incentive || 0) > (seen[key].incentive || 0)) seen[key] = r;
      });
      setOpenShifts(Object.values(seen));
    }
  }

  // ── Chat with manager ──
  async function fetchChatMessages() {
    if (!myEmployee) return;
    const { data } = await supabase.from('messages').select('*')
      .eq('company_id', myEmployee.company_id)
      .or(`sender_id.eq.${myEmployee.id},recipient_id.eq.${myEmployee.id}`)
      .order('created_at', { ascending: true });
    const msgs = (data || []).filter(m => !(m.sender_id === 0 && m.recipient_id === 0)); // exclude broadcasts
    setChatMessages(msgs);
    // Mark manager-sent messages as read
    const unread = msgs.filter(m => m.sender_id === 0 && !m.read && m.recipient_id === myEmployee.id);
    if (unread.length > 0) {
      await supabase.from('messages').update({ read: true })
        .eq('company_id', myEmployee.company_id)
        .eq('recipient_id', myEmployee.id)
        .eq('read', false);
    }
    setChatUnread(0);
  }

  async function fetchChatUnread() {
    if (!myEmployee) return;
    const { data } = await supabase.from('messages').select('id')
      .eq('company_id', myEmployee.company_id)
      .eq('recipient_id', myEmployee.id)
      .eq('read', false);
    setChatUnread(data?.length || 0);
  }

  async function sendChatMessage() {
    if (!chatInput.trim() || !myEmployee) return;
    await supabase.from('messages').insert([{
      company_id: myEmployee.company_id,
      sender_id: myEmployee.id,
      recipient_id: 0,
      message: chatInput.trim(),
      read: false,
    }]);
    setChatInput('');
    fetchChatMessages();
  }

  useEffect(() => {
    if (activeTab === 'messages' && myEmployee) fetchChatMessages();
  }, [activeTab, myEmployee]);

  // Fetch unread count on mount and when switching tabs
  useEffect(() => {
    if (myEmployee) fetchChatUnread();
  }, [myEmployee, activeTab]);

  function formatMsgTime(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // ── Shift swap functions ──
  function getTeamWeekDates(offset = 0) {
    const now = new Date(); const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now); monday.setDate(diff + offset * 7); monday.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  }
  const teamWeekDates = getTeamWeekDates(teamWeekOffset);
  const teamWeekLabel = `${teamWeekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${teamWeekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  async function fetchSwapData() {
    if (!myEmployee) return;
    const dateStrings = teamWeekDates.map(d => d.toISOString().split('T')[0]);
    const { data: allShifts } = await supabase.from('shifts').select('*').eq('company_id', myEmployee.company_id).in('day', dateStrings);
    setTeamShifts(allShifts || []);
    const { data: swaps } = await supabase.from('swap_requests').select('*').eq('company_id', myEmployee.company_id);
    if (swaps) {
      setAllSwapRequests(swaps);
      setIncomingSwaps(swaps.filter(s => s.target_id === myEmployee.id && s.status === 'pending_employee'));
      setOutgoingSwaps(swaps.filter(s => s.requester_id === myEmployee.id).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setSwapBadge(swaps.filter(s => s.target_id === myEmployee.id && s.status === 'pending_employee').length);
    }
  }

  useEffect(() => {
    if ((activeTab === 'team' || activeTab === 'request') && myEmployee) fetchSwapData();
  }, [activeTab, myEmployee, teamWeekOffset]);

  function isShiftPendingSwap(shiftId) {
    return allSwapRequests.some(s => ['pending_employee', 'pending_manager'].includes(s.status) && (s.requester_shift_id === shiftId || s.target_shift_id === shiftId));
  }

  function showToast(msg) { setSwapToast(msg); setTimeout(() => setSwapToast(''), 3000); }

  async function proposeSwap() {
    if (!swapTarget || !myEmployee || swapSubmitting) return;
    if (swapMode === 'trade' && !swapMyShift) return;
    setSwapError('');
    setSwapSubmitting(true);
    const target = swapTarget;
    const mine = swapMyShift;
    const targetEmp = employees.find(e => String(e.id) === String(target.shift.employee_id));

    // Duplicate check
    const existing = allSwapRequests.filter(s => ['pending_employee', 'pending_manager'].includes(s.status));
    if (existing.some(s => s.target_shift_id === target.shift.id)) {
      setSwapError('This shift already has a pending request.'); setSwapSubmitting(false); return;
    }
    if (swapMode === 'trade' && mine && existing.some(s => s.requester_shift_id === mine.id || s.target_shift_id === mine.id)) {
      setSwapError('Your offered shift already has a pending request.'); setSwapSubmitting(false); return;
    }

    const row = {
      company_id: myEmployee.company_id,
      type: swapMode === 'pickup' ? 'pickup' : 'swap',
      status: 'pending_employee',
      requester_id: myEmployee.id,
      requester_name: myEmployee.name,
      target_id: target.shift.employee_id,
      target_name: targetEmp?.name || 'Unknown',
      target_shift_id: target.shift.id,
      target_shift_day: target.shift.day,
      target_shift_period: target.shift.shift_name,
      target_shift_role: target.shift.role,
      note: swapNote || null,
    };
    if (swapMode === 'trade' && mine) {
      row.requester_shift_id = mine.id;
      row.requester_shift_day = mine.day;
      row.requester_shift_period = mine.shift_name;
      row.requester_shift_role = mine.role;
    }
    const { error } = await supabase.from('swap_requests').insert([row]);
    if (error) { setSwapError('Failed to send request: ' + error.message); setSwapSubmitting(false); return; }

    // Notify target employee
    const msg = swapMode === 'pickup'
      ? `${myEmployee.name} is requesting to take your ${target.shift.shift_name} shift on ${target.shift.day}. Open the app to respond.${swapNote ? '\nNote: ' + swapNote : ''}`
      : `${myEmployee.name} wants to swap shifts with you!\nTheir ${mine.shift_name} (${mine.day}) \u21C4 Your ${target.shift.shift_name} (${target.shift.day})${swapNote ? '\nNote: ' + swapNote : ''}`;
    await supabase.from('messages').insert([{ company_id: myEmployee.company_id, sender_id: myEmployee.id, recipient_id: target.shift.employee_id, message: msg, read: false }]);

    const toastMsg = swapMode === 'pickup' ? `Pickup request sent to ${targetEmp?.name || 'employee'}!` : `Trade request sent to ${targetEmp?.name || 'employee'}!`;
    setSwapTarget(null); setSwapMyShift(null); setSwapNote(''); setSwapMode(null); setSwapSubmitting(false); setSwapError('');
    showToast(toastMsg);
    fetchSwapData();
  }

  async function respondToSwap(swapId, accept) {
    const swap = allSwapRequests.find(s => s.id === swapId);
    if (!swap) return;
    if (accept) {
      await supabase.from('swap_requests').update({ status: 'pending_manager' }).eq('id', swapId);
      // Notify requester
      await supabase.from('messages').insert([{ company_id: myEmployee.company_id, sender_id: myEmployee.id, recipient_id: swap.requester_id, message: `${myEmployee.name} accepted your ${swap.type === 'pickup' ? 'pickup' : 'swap'} request! Waiting for manager approval.`, read: false }]);
      // Notify manager
      const detail = swap.type === 'pickup'
        ? `${swap.requester_name} wants to take ${swap.target_name}'s ${swap.target_shift_period} on ${swap.target_shift_day}. ${swap.target_name} has agreed. Awaiting your approval.`
        : `${swap.requester_name} and ${swap.target_name} have agreed to swap shifts. Awaiting your approval.`;
      await supabase.from('messages').insert([{ company_id: myEmployee.company_id, sender_id: myEmployee.id, recipient_id: 0, message: detail, read: false }]);
    } else {
      await supabase.from('swap_requests').update({ status: 'denied' }).eq('id', swapId);
      await supabase.from('messages').insert([{ company_id: myEmployee.company_id, sender_id: myEmployee.id, recipient_id: swap.requester_id, message: `${myEmployee.name} declined your ${swap.type === 'pickup' ? 'pickup' : 'swap'} request.`, read: false }]);
    }
    fetchSwapData();
  }

  async function cancelSwap(swapId) {
    const swap = allSwapRequests.find(s => s.id === swapId);
    if (!swap) return;
    await supabase.from('swap_requests').update({ status: 'cancelled' }).eq('id', swapId);
    await supabase.from('messages').insert([{ company_id: myEmployee.company_id, sender_id: myEmployee.id, recipient_id: swap.target_id, message: `${myEmployee.name} cancelled their ${swap.type === 'pickup' ? 'pickup' : 'swap'} request.`, read: false }]);
    fetchSwapData();
  }

  const [canOpen, setCanOpen] = useState(true);
  const [canClose, setCanClose] = useState(true);
  const [prefersDoubles, setPrefersDoubles] = useState(false);
  const [noClopens, setNoClopens] = useState(true);
  const [wantsMoreHours, setWantsMoreHours] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Load preferences when employee data is available
  useEffect(() => {
    if (myEmployee) {
      setCanOpen(myEmployee.can_open !== false);
      setCanClose(myEmployee.can_close !== false);
      setPrefersDoubles(myEmployee.prefers_doubles || false);
      setNoClopens(myEmployee.no_clopens !== false);
      setWantsMoreHours(myEmployee.wants_more_hours || false);
      setPrefShifts((myEmployee.preferred_shifts || myEmployee.preferred_shift || '').split(',').map(s => s.trim()).filter(Boolean));
      setPrefDays((myEmployee.preferred_days || '').split(',').map(s => s.trim()).filter(Boolean));
    }
  }, [myEmployee]);

  async function savePreferences(updates) {
    if (!myEmployee) return;
    await supabase.from('employees').update(updates).eq('id', myEmployee.id);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 1500);
  }

  async function saveOnboarding() {
    await supabase.from('employees').update({
      preferred_shifts: prefShifts.join(','),
      preferred_days: prefDays.join(','),
      max_hours_per_week: maxHours,
      certifications: certs.join(','),
      emergency_contact: emergencyContact,
      employee_notes: employeeNotes,
      can_open: canOpen,
      can_close: canClose,
      prefers_doubles: prefersDoubles,
      no_clopens: noClopens,
      profile_completed: true,
    }).eq('id', myEmployee.id);
    setShowOnboarding(false);
    fetchData();
  }

  function togglePref(arr, setArr, val) {
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  }

  function findAvailableCoworkers(shift) {
    return employees.filter(e => {
      if (e.id === myEmployee?.id) return false;
      const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []);
      const avail = (e.availability || '').split(',').map(d => d.trim());
      const shiftDay = new Date(shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
      return roles.includes(shift.role) && avail.some(d => shiftDay.startsWith(d));
    });
  }

  async function getAiRankings(shift, coworkers) {
    if (coworkers.length === 0) return;
    setRankingLoading(true);
    const weekShiftCounts = {};
    employees.forEach(e => { weekShiftCounts[e.id] = 0; });
    shifts.forEach(s => { if (weekShiftCounts[s.employee_id] !== undefined) weekShiftCounts[s.employee_id]++; });

    const cwData = coworkers.map(cw => ({
      id: cw.id,
      name: cw.name,
      role: cw.role,
      preferred_shifts: cw.preferred_shifts || '',
      target_hours: cw.target_hours || 30,
      current_hours_this_week: (weekShiftCounts[cw.id] || 0) * 7,
      preferred_days: cw.preferred_days || '',
      certifications: cw.certifications || '',
    }));

    const prompt = `You are a restaurant scheduling assistant. Rank these employees for covering a ${shift.shift_name} shift on ${shift.day} as a ${shift.role}.

Available employees:
${JSON.stringify(cwData, null, 2)}

For each employee, give a short 1-line reason why they are or aren't the best fit. Consider: preferred shift match, hours needed vs current hours, preferred days.

Return ONLY a JSON array ordered best to worst:
[{"id": <number>, "name": "<name>", "score": <1-10>, "reason": "<one line reason>", "tag": "<Best match|Good option|Available>"}]`;

    try {
      const response = await fetch('/api/generate-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 500,
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      const data = await response.json();
      const text = data.content?.[0]?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      const rankings = JSON.parse(clean);
      setAiRankings(rankings);
    } catch (e) {
      console.error('AI ranking failed:', e);
    }
    setRankingLoading(false);
  }

  async function openReleaseModal(shift) {
    setReleaseShift(shift);
    const coworkers = findAvailableCoworkers(shift);
    setAvailableCoworkers(coworkers);
    setReleaseStep(1);
    setAiRankings([]);
    setShowReleaseModal(true);
  }

  async function goToStep2() {
    setReleaseStep(2);
    await getAiRankings(releaseShift, availableCoworkers);
  }

  async function releaseShiftSubmit() {
    if (!releaseShift) return;
    // Check if already released in either table
    const { data: existingRel } = await supabase.from('shift_releases').select('id').eq('shift_id', releaseShift.id).in('status', ['open', 'claimed']).limit(1);
    if (existingRel && existingRel.length > 0) {
      setShowReleaseModal(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      await fetchData();
      return;
    }
    const incentiveAmt = parseFloat(releaseIncentive) || 0;
    // 1. Save to shift_releases table (for the open shifts marketplace)
    const { error: relErr } = await supabase.from('shift_releases').insert([{
      company_id: releaseShift.company_id,
      shift_id: releaseShift.id,
      employee_id: myEmployee.id,
      employee_name: myEmployee.name,
      shift_date: releaseShift.day,
      shift_name: releaseShift.shift_name,
      role: releaseShift.role,
      reason: releaseReason,
      incentive: incentiveAmt,
      status: 'open',
    }]);
    console.log('SHIFT RELEASE INSERT:', { error: relErr, shift_id: releaseShift.id });
    if (relErr) console.error('RELEASE INSERT ERROR:', relErr);
    // 2. Save to requests table (for manager approval queue)
    const detailStr = `Release ${releaseShift.shift_name} on ${releaseShift.day} (${releaseShift.role})${incentiveAmt > 0 ? ` — $${incentiveAmt} offered` : ''}`;
    const { data: reqData, error: reqErr } = await supabase.from('requests').insert([{
      company_id: releaseShift.company_id,
      employee_name: myEmployee.name,
      initials: myEmployee.initials,
      color: myEmployee.color,
      type: 'shift_release',
      detail: detailStr,
      note: releaseReason || null,
      status: 'pending',
    }]).select();
    console.log('REQUEST INSERT:', { data: reqData, error: reqErr });
    if (reqErr) alert('Could not notify manager: ' + reqErr.message);
    // 3. Notify manager via messages
    await supabase.from('messages').insert([{
      company_id: releaseShift.company_id,
      sender_id: myEmployee.id,
      recipient_id: 0,
      message: `${myEmployee.name} needs to release their ${releaseShift.shift_name} shift on ${releaseShift.day}${releaseReason ? ': ' + releaseReason : ''}${incentiveAmt > 0 ? ` — offering $${incentiveAmt}` : ''}`,
      read: false,
    }]);
    setShowReleaseModal(false);
    setReleaseReason(''); setReleaseIncentive(''); setReleaseStep(1);
    setSubmitted(true);
    await fetchData();
    setTimeout(() => setSubmitted(false), 5000);
  }

  async function updateReleaseIncentive(shiftId, releaseRecord) {
    const amt = parseFloat(newIncentiveVal) || 0;
    // Update shift_releases
    if (releaseRecord && !releaseRecord._fromRequests) {
      await supabase.from('shift_releases').update({ incentive: amt }).eq('id', releaseRecord.id);
    }
    // Update the matching request's detail string
    const { data: reqs } = await supabase.from('requests').select('id, detail').eq('company_id', myEmployee.company_id).eq('employee_name', myEmployee.name).eq('type', 'shift_release').eq('status', 'pending');
    const matchReq = (reqs || []).find(r => r.detail?.includes(releaseRecord.shift_date || ''));
    if (matchReq) {
      const newDetail = matchReq.detail.replace(/( — \$[\d.]+ offered)?$/, amt > 0 ? ` — $${amt} offered` : '');
      await supabase.from('requests').update({ detail: newDetail }).eq('id', matchReq.id);
    }
    setEditIncentive(null);
    await fetchData();
  }

  function getShiftConflict(shiftDate, shiftName) {
    // Check if employee has any shift on this date
    const dayShifts = shifts.filter(s => s.day === shiftDate);
    if (dayShifts.length === 0) return { status: 'clear', message: 'Fits your schedule', color: 'green' };
    const samePeriod = dayShifts.find(s => s.shift_name === shiftName);
    if (samePeriod) return { status: 'conflict', message: `Conflict — you already have a ${shiftName} shift this day`, color: 'red' };
    const other = dayShifts[0];
    return { status: 'double', message: `You already have a shift this day (${other.shift_name} ${formatTime(other.start_time)}–${formatTime(other.end_time)}) — this would be a double`, color: 'yellow' };
  }

  async function submitPickup(release) {
    if (!myEmployee) return;
    // Save pickup request — use only columns known to exist
    const { error } = await supabase.from('requests').insert([{
      company_id: myEmployee.company_id,
      employee_name: myEmployee.name,
      initials: myEmployee.initials,
      color: myEmployee.color,
      type: 'shift_pickup',
      detail: `Pickup ${release.shift_name} on ${release.shift_date} (${release.role}) from ${release.employee_name}`,
      note: pickupNote || null,
      status: 'pending',
    }]);
    if (error) { console.error('PICKUP INSERT ERROR:', error); return; }
    // Mark ALL open releases for this shift as claimed (handles duplicates)
    await supabase.from('shift_releases').update({ status: 'claimed', covered_by: myEmployee.id }).eq('shift_id', release.shift_id).eq('status', 'open');
    await supabase.from('messages').insert([{
      company_id: release.company_id, sender_id: myEmployee.id, recipient_id: 0,
      message: `${myEmployee.name} wants to pick up ${release.employee_name}'s ${release.shift_name} shift on ${release.shift_date}`,
      read: false,
    }]);
    setPickupSubmitted(prev => ({ ...prev, [release.id]: true }));
    setPickupNote('');
    fetchData();
  }

  async function submitRequest() {
    if (!requestType || !myEmployee) return;
    await supabase.from('requests').insert([{
      company_id: myEmployee.company_id,
      employee_name: myEmployee.name,
      initials: myEmployee.initials,
      color: myEmployee.color,
      type: requestType,
      detail: requestType,
      note: requestNote,
      status: 'pending',
    }]);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setRequestType(''); setRequestNote(''); }, 3000);
  }

  async function submitTimeOff() {
    if (timeOffDates.length === 0 || !myEmployee) return;
    const sortedDates = [...timeOffDates].sort();
    // Use only columns known to exist in the requests table
    const payload = {
      company_id: myEmployee.company_id,
      employee_name: myEmployee.name,
      initials: myEmployee.initials,
      color: myEmployee.color,
      type: 'time_off',
      detail: `Time off: ${sortedDates.join(', ')}`,
      note: timeOffNote ? `${timeOffNote} | Dates: ${sortedDates.join(', ')}` : `Dates: ${sortedDates.join(', ')}`,
      status: 'pending',
    };
    console.log('TIME OFF SUBMIT:', payload);
    const { error } = await supabase.from('requests').insert([payload]);
    if (error) { console.error('TIME OFF INSERT ERROR:', error); alert('Failed to submit request. Check console.'); return; }
    setTimeOffSubmitted(true);
    setTimeout(() => { setTimeOffSubmitted(false); setShowTimeOff(false); setTimeOffDates([]); setTimeOffNote(''); }, 2500);
  }

  function toggleTimeOffDate(dateStr) {
    setTimeOffDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]);
  }

  function getCalendarDays(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }

  function formatShiftDate(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  const name = myEmployee?.name?.split(' ')[0] || 'there';
  const initials = myEmployee?.initials || '?';
  const color = myEmployee?.color || 'bg-blue-100 text-blue-700';

  return (
    <div className="min-h-screen bg-gray-50 flex justify-center">
      <div className="w-full max-w-sm bg-white min-h-screen flex flex-col">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-3">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-lg font-medium text-gray-900">Hey, {name} 👋</div>
              <div className="text-xs text-gray-400">The Rustic Table · {typeof myEmployee?.role === 'string' ? myEmployee.role.split(',')[0] : ''}</div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${color}`}>{initials}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          {['schedule', 'open', 'request', 'messages', 'profile', 'team'].map(tab => (
            <button key={tab} onClick={() => { setActiveTab(tab); if (tab === 'open' || tab === 'schedule') fetchData(); if (tab === 'messages') fetchChatMessages(); if (tab === 'team') fetchSwapData(); }}
              className={`flex-1 py-3 text-xs font-medium border-b-2 relative ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400'}`}>
              {tab === 'open' ? 'Open shifts' : tab === 'messages' ? 'Messages' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'open' && openShifts.length > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center" style={{ fontSize: '9px' }}>{openShifts.length}</span>
              )}
              {tab === 'messages' && chatUnread > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center" style={{ fontSize: '9px' }}>{chatUnread}</span>
              )}
              {tab === 'request' && swapBadge > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center" style={{ fontSize: '9px' }}>{swapBadge}</span>
              )}
              {tab === 'team' && swapBadge > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center" style={{ fontSize: '9px' }}>{swapBadge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Schedule tab */}
        {activeTab === 'schedule' && (
          <div className="flex-1 overflow-y-auto p-4">
            {submitted && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center mb-4">
                <div className="text-sm font-medium text-green-700">✓ Shift release submitted!</div>
                <div className="text-xs text-green-500 mt-0.5">Your manager has been notified.</div>
              </div>
            )}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">This week</div>
            {shifts.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-8">No shifts scheduled this week</div>
            ) : (
              <div className="flex flex-col gap-3">
                {shifts.map(s => {
                  const release = myReleases[s.id];
                  return (
                    <div key={s.id} className={`rounded-xl border p-4 ${release ? 'border-orange-200 bg-orange-50/30' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">{formatShiftDate(s.day)}</div>
                        <div className="flex items-center gap-1.5">
                          {release && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Release Requested ⏳</span>}
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{s.role}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{s.shift_name} · {formatTime(s.start_time)}–{formatTime(s.end_time)}</div>
                      {release ? (
                        <div className="mt-3">
                          <div className="text-xs text-gray-400 mb-2">
                            Waiting for coverage{release.incentive > 0 ? ` · $${release.incentive} offered` : ''}
                          </div>
                          {editIncentive === s.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400">$</span>
                              <input type="number" value={newIncentiveVal} onChange={e => setNewIncentiveVal(e.target.value)}
                                className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-white" placeholder="0" />
                              <button onClick={() => updateReleaseIncentive(s.id, release)}
                                className="text-xs bg-blue-600 text-white rounded-lg px-3 py-1.5">Save</button>
                              <button onClick={() => setEditIncentive(null)} className="text-xs text-gray-400">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => { setEditIncentive(s.id); setNewIncentiveVal(String(release.incentive || '')); }}
                              className="text-xs border border-green-200 text-green-600 rounded-lg px-3 py-1.5 hover:bg-green-50">
                              {release.incentive > 0 ? 'Update offer' : 'Add incentive'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => openReleaseModal(s)}
                            className="text-xs border border-red-200 text-red-500 rounded-lg px-3 py-1.5 hover:bg-red-50">
                            Can't work this
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="text-xs font-medium text-blue-700 mb-1">This week</div>
              <div className="text-2xl font-medium text-blue-900">{shifts.length * 7} hrs</div>
              <div className="text-xs text-blue-500 mt-0.5">Target: {myEmployee?.target_hours || 30} hrs</div>
            </div>
          </div>
        )}

        {/* Request tab */}
        {activeTab === 'request' && (
          <div className="flex-1 overflow-y-auto p-4">
            {/* Incoming swap requests on Request tab */}
            {incomingSwaps.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Incoming swap requests</div>
                {incomingSwaps.map(swap => {
                  const requester = employees.find(e => String(e.id) === String(swap.requester_id));
                  const isPickup = swap.type === 'pickup';
                  return (
                    <div key={swap.id} className={`bg-white rounded-xl border-2 p-4 mb-2 ${isPickup ? 'border-blue-200' : 'border-amber-200'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${requester?.color || 'bg-gray-100 text-gray-600'}`}>{requester?.initials || '?'}</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{swap.requester_name}</div>
                          <div className="text-xs text-gray-400">{isPickup ? `wants your ${swap.target_shift_period} on ${swap.target_shift_day}` : `swap: their ${swap.requester_shift_period} ${swap.requester_shift_day} \u21C4 your ${swap.target_shift_period} ${swap.target_shift_day}`}</div>
                        </div>
                      </div>
                      {isPickup && <div className="text-xs text-red-400 mb-2">You will lose this shift — no trade in return.</div>}
                      {swap.note && <div className="text-xs text-gray-400 italic mb-2">"{swap.note}"</div>}
                      <div className="flex gap-2">
                        <button onClick={() => respondToSwap(swap.id, true)} className="flex-1 py-2 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700">{isPickup ? 'Give up shift' : 'Accept'}</button>
                        <button onClick={() => respondToSwap(swap.id, false)} className="flex-1 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50">Decline</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Your outgoing swap requests */}
            {outgoingSwaps.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Your swap requests</div>
                {outgoingSwaps.slice(0, 10).map(swap => {
                  const statusLabel = swap.status === 'pending_employee' ? `Waiting for ${swap.target_name}` : swap.status === 'pending_manager' ? 'Waiting for manager' : swap.status === 'approved' ? 'Approved' : swap.status === 'denied' ? 'Declined' : 'Cancelled';
                  const statusColor = swap.status === 'pending_employee' ? 'bg-amber-50 text-amber-600 border-amber-200' : swap.status === 'pending_manager' ? 'bg-blue-50 text-blue-600 border-blue-200' : swap.status === 'approved' ? 'bg-green-50 text-green-600 border-green-200' : swap.status === 'denied' ? 'bg-red-50 text-red-500 border-red-200' : 'bg-gray-50 text-gray-400 border-gray-200';
                  const canCancel = ['pending_employee', 'pending_manager'].includes(swap.status);
                  return (
                    <div key={swap.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3 mb-1.5 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{swap.type === 'pickup' ? `Pickup ${swap.target_shift_period} ${swap.target_shift_day}` : `Swap with ${swap.target_name}`}</div>
                        <div className="text-xs text-gray-400 truncate">{swap.type === 'swap' ? `Your ${swap.requester_shift_period} ${swap.requester_shift_day} \u21C4 Their ${swap.target_shift_period} ${swap.target_shift_day}` : `from ${swap.target_name}`}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border whitespace-nowrap ${statusColor}`}>{statusLabel}</span>
                      {canCancel && <button onClick={() => cancelSwap(swap.id)} className="text-xs text-red-400 hover:text-red-600 flex-shrink-0">Cancel</button>}
                    </div>
                  );
                })}
              </div>
            )}

            <button onClick={() => { setShowTimeOff(true); setTimeOffDates([]); setTimeOffNote(''); setTimeOffSubmitted(false); }}
              className="w-full mb-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-100">
              Request Time Off
            </button>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">Other requests</div>
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-2xl mb-2">✓</div>
                <div className="text-sm font-medium text-green-700">Request sent!</div>
                <div className="text-xs text-green-500 mt-1">Your manager will review it shortly.</div>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2 mb-4">
                  {['Shift swap', 'More hours', 'Fewer hours'].map(type => (
                    <button key={type} onClick={() => setRequestType(type)}
                      className={`px-4 py-3 rounded-xl border text-sm text-left ${requestType === type ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {type}
                    </button>
                  ))}
                </div>
                {requestType && (
                  <>
                    <div className="mb-4">
                      <label className="text-xs font-medium text-gray-400 block mb-1.5">Note to manager (optional)</label>
                      <textarea value={requestNote} onChange={e => setRequestNote(e.target.value)}
                        placeholder="Any details your manager should know..." rows={3}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none" />
                    </div>
                    <button onClick={submitRequest}
                      className="w-full bg-blue-600 text-white rounded-xl py-3 text-sm font-medium hover:bg-blue-700">
                      Send request
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Open shifts tab */}
        {activeTab === 'open' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Open shifts for your role</div>
              <button onClick={() => fetchData()} className="text-xs text-gray-400 hover:text-gray-600">↻ Refresh</button>
            </div>
            {openShifts.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-sm text-gray-400">No open shifts right now</div>
                <div className="text-xs text-gray-300 mt-1">We'll show shifts here that match your role</div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {openShifts.map(r => {
                  const conflict = getShiftConflict(r.shift_date, r.shift_name);
                  const alreadyRequested = pickupSubmitted[r.id];
                  return (
                    <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">{formatShiftDate(r.shift_date)}</div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{r.role}</span>
                      </div>
                      <div className="text-sm text-gray-600">{r.shift_name}</div>
                      <div className="text-xs text-gray-400 mt-1">Released by {r.employee_name}</div>
                      {r.reason && <div className="text-xs text-gray-400 italic mt-0.5">"{r.reason}"</div>}
                      {r.incentive > 0 && (
                        <div className="mt-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs text-green-700">
                          💰 ${r.incentive} offered to pick up this shift
                        </div>
                      )}

                      {/* Conflict check */}
                      <div className={`mt-2 flex items-center gap-1.5 text-xs ${conflict.color === 'green' ? 'text-green-600' : conflict.color === 'yellow' ? 'text-amber-600' : 'text-red-500'}`}>
                        <span>{conflict.color === 'green' ? '✓' : conflict.color === 'yellow' ? '⚠' : '✕'}</span>
                        <span>{conflict.message}</span>
                      </div>

                      {alreadyRequested ? (
                        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 text-center">
                          ✓ Request sent to manager for approval
                        </div>
                      ) : conflict.status === 'conflict' ? (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-500 text-center">
                          Can't request — same period conflict
                        </div>
                      ) : (
                        <div className="mt-3">
                          <input type="text" placeholder="Add a note for the manager (optional)" value={pickupNote}
                            onChange={e => setPickupNote(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-400 bg-gray-50 mb-2" />
                          <button onClick={() => submitPickup(r)}
                            className={`w-full rounded-lg py-2 text-xs font-medium ${conflict.status === 'double' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                            {conflict.status === 'double' ? 'Request anyway (double shift)' : 'Request this shift'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Team tab */}
        {/* Messages tab */}
        {activeTab === 'messages' && (
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <div className="flex-1 overflow-y-auto p-4" style={{ display: 'flex', flexDirection: 'column' }}>
              {chatMessages.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl mb-2">💬</div>
                    <div className="text-sm text-gray-400">No messages yet</div>
                    <div className="text-xs text-gray-300 mt-1">Send a message to your manager</div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                  {chatMessages.map((msg, i) => {
                    const isMe = msg.sender_id === myEmployee?.id;
                    const showDate = i === 0 || new Date(msg.created_at).toDateString() !== new Date(chatMessages[i - 1].created_at).toDateString();
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="text-center my-2">
                            <span className="text-xs text-gray-300 bg-gray-50 px-3 py-1 rounded-full">{new Date(msg.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            maxWidth: '80%', padding: '10px 14px', borderRadius: 16,
                            background: isMe ? '#2563EB' : '#F3F4F6',
                            color: isMe ? '#FFF' : '#1F2937',
                            borderBottomRightRadius: isMe ? 4 : 16,
                            borderBottomLeftRadius: isMe ? 16 : 4,
                          }}>
                            <div className="text-sm" style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                            <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: isMe ? 'right' : 'left' }}>{formatMsgTime(msg.created_at)}</div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 p-3 bg-white flex gap-2">
              <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                placeholder="Type a message..."
                className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
              <button onClick={sendChatMessage} disabled={!chatInput.trim()}
                className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40"
                style={{ fontSize: 16 }}>↑</button>
            </div>
          </div>
        )}

        {/* Profile tab */}
        {activeTab === 'profile' && (
          <div className="flex-1 overflow-y-auto p-4">
            {profileSaved && <div className="bg-green-50 border border-green-200 rounded-xl p-2.5 text-center mb-3 text-xs font-medium text-green-700">{'\u2713'} Saved</div>}

            {/* Shift Preferences */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Shift preferences</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { val: canOpen, set: setCanOpen, key: 'can_open', label: 'I can open', desc: 'Trained to open the restaurant' },
                { val: canClose, set: setCanClose, key: 'can_close', label: 'I can close', desc: 'Trained to close the restaurant' },
                { val: prefersDoubles, set: setPrefersDoubles, key: 'prefers_doubles', label: 'Prefer doubles', desc: 'I like working two shifts in one day' },
                { val: noClopens, set: setNoClopens, key: 'no_clopens', label: 'No clopens', desc: "Don't close then open next morning" },
              ].map(({ val, set, key, label, desc }) => (
                <button key={key} onClick={() => { set(!val); savePreferences({ [key]: !val }); }}
                  className={`text-left px-3 py-2.5 rounded-xl border text-xs ${val ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className={`font-medium ${val ? 'text-blue-700' : 'text-gray-500'}`}>{val ? '\u2713 ' : ''}{label}</div>
                  <div className="text-gray-400 mt-0.5" style={{ fontSize: 10 }}>{desc}</div>
                </button>
              ))}
            </div>

            {/* Preferred shifts */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Shifts I prefer</div>
            <div className="grid grid-cols-4 gap-1 mb-4">
              {SHIFT_PREFS.map(s => {
                const active = prefShifts.includes(s);
                return (
                  <button key={s} onClick={() => {
                    const newPrefs = active ? prefShifts.filter(p => p !== s) : [...prefShifts, s];
                    setPrefShifts(newPrefs);
                    savePreferences({ preferred_shifts: newPrefs.join(',') });
                  }}
                    className={`py-2 rounded-xl border text-xs font-medium ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                    {s}
                  </button>
                );
              })}
            </div>

            {/* Preferred days */}
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Days I prefer</div>
            <div className="grid grid-cols-7 gap-1 mb-4">
              {DAYS.map(d => {
                const active = prefDays.includes(d);
                return (
                  <button key={d} onClick={() => {
                    const newDays = active ? prefDays.filter(x => x !== d) : [...prefDays, d];
                    setPrefDays(newDays);
                    savePreferences({ preferred_days: newDays.join(',') });
                  }}
                    className={`py-2 rounded-xl border text-xs font-medium ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                    {d}
                  </button>
                );
              })}
            </div>

            {/* Wants more hours */}
            <button onClick={() => { setWantsMoreHours(!wantsMoreHours); savePreferences({ wants_more_hours: !wantsMoreHours }); }}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm mb-4 ${wantsMoreHours ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`font-medium ${wantsMoreHours ? 'text-green-700' : 'text-gray-500'}`}>{wantsMoreHours ? '\u2713 ' : ''}I want more hours</div>
              <div className="text-xs text-gray-400 mt-0.5">Let your manager know you're available for extra shifts</div>
            </button>

            {/* Read-only unavailability from manager */}
            {myEmployee && Array.isArray(myEmployee.unavailable) && myEmployee.unavailable.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                <div className="font-medium mb-1">Your manager has set restrictions:</div>
                {myEmployee.unavailable.map((u, i) => (
                  <div key={i}>{u.day}: {u.allDay ? 'all day' : (u.periods || []).join(', ')}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (() => {
          const myRoles = myEmployee?.role ? myEmployee.role.split(',').map(r => r.trim()) : [];
          const allTeamRoles = [...new Set(teamShifts.map(s => s.role).filter(Boolean))].sort();
          const filteredShifts = teamShifts.filter(s => {
            if (teamRoleFilter === 'all') return true;
            if (teamRoleFilter === 'my') return myRoles.includes(s.role);
            return s.role === teamRoleFilter;
          });
          const allEmps = [myEmployee, ...employees.filter(e => e.id !== myEmployee?.id)].filter(Boolean);
          return (
          <div className="flex-1 overflow-y-auto p-4">
            {/* Toast */}
            {swapToast && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center mb-3 text-xs font-medium text-green-700">{swapToast}</div>}

            {/* Incoming swap/pickup requests */}
            {incomingSwaps.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Incoming requests</div>
                {incomingSwaps.map(swap => {
                  const requester = employees.find(e => String(e.id) === String(swap.requester_id));
                  const isPickup = swap.type === 'pickup';
                  return (
                    <div key={swap.id} className={`bg-white rounded-xl border-2 p-4 mb-2 ${isPickup ? 'border-blue-200' : 'border-amber-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${requester?.color || 'bg-gray-100 text-gray-600'}`}>{requester?.initials || '?'}</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{swap.requester_name}</div>
                          <div className="text-xs text-gray-400">{isPickup ? 'wants to take your shift' : 'wants to swap shifts'}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPickup ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>{isPickup ? 'Pickup' : 'Trade'}</span>
                      </div>
                      {isPickup ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-center">
                          <div className="text-red-600 font-medium mb-0.5">They want your shift</div>
                          <div className="text-gray-600">{swap.target_shift_period} on {swap.target_shift_day} ({swap.target_shift_role})</div>
                          <div className="text-red-400 mt-1">You will lose this shift if you accept</div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mb-3 text-xs">
                          <div className="flex-1 bg-gray-50 rounded-lg p-2.5 text-center">
                            <div className="text-gray-400 mb-0.5">Their shift</div>
                            <div className="font-medium text-gray-800">{swap.requester_shift_period} {swap.requester_shift_day} ({swap.requester_shift_role})</div>
                          </div>
                          <div className="text-gray-300 text-lg">&#x21C4;</div>
                          <div className="flex-1 bg-gray-50 rounded-lg p-2.5 text-center">
                            <div className="text-gray-400 mb-0.5">Your shift</div>
                            <div className="font-medium text-gray-800">{swap.target_shift_period} {swap.target_shift_day} ({swap.target_shift_role})</div>
                          </div>
                        </div>
                      )}
                      {swap.note && <div className="text-xs text-gray-400 italic mb-3">"{swap.note}"</div>}
                      <div className="flex gap-2">
                        <button onClick={() => respondToSwap(swap.id, true)} className="flex-1 py-2 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700">{isPickup ? 'Give up shift' : 'Accept trade'}</button>
                        <button onClick={() => respondToSwap(swap.id, false)} className="flex-1 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50">Decline</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Week navigation */}
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setTeamWeekOffset(w => Math.max(w - 1, -4))} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:bg-gray-50 text-xs">&#x2190;</button>
              <div className="text-xs font-medium text-gray-600 text-center">{teamWeekLabel}</div>
              <button onClick={() => setTeamWeekOffset(w => Math.min(w + 1, 8))} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:bg-gray-50 text-xs">&#x2192;</button>
            </div>

            {/* Role filter - scrollable */}
            <div className="flex items-center gap-2 mb-3" style={{ overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
              <style>{`.role-scroll::-webkit-scrollbar{display:none}`}</style>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 flex-nowrap role-scroll" style={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
                <button onClick={() => setTeamRoleFilter('my')} className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${teamRoleFilter === 'my' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'}`}>My roles</button>
                <button onClick={() => setTeamRoleFilter('all')} className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${teamRoleFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'}`}>All</button>
                {allTeamRoles.map(r => (
                  <button key={r} onClick={() => setTeamRoleFilter(r)} className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${teamRoleFilter === r ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'}`}>{r}</button>
                ))}
              </div>
            </div>

            {/* Schedule grid - includes self at top */}
            <div className="flex flex-col gap-2">
              {allEmps.map(emp => {
                const isMe = emp.id === myEmployee?.id;
                const empShifts = filteredShifts.filter(s => String(s.employee_id) === String(emp.id));
                if (empShifts.length === 0 && !isMe) return null;
                return (
                  <div key={emp.id} className={`rounded-xl border p-3 ${isMe ? 'bg-blue-50/40 border-blue-200' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${emp.color}`} style={{ fontSize: 9 }}>{emp.initials}</div>
                      <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                      {isMe && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">You</span>}
                      <div className="text-xs text-gray-400">{typeof emp.role === 'string' ? emp.role.split(',')[0].trim() : ''}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {empShifts.length === 0 && <div className="text-xs text-gray-300">No shifts this week</div>}
                      {empShifts.map(shift => {
                        const isPending = isShiftPendingSwap(shift.id);
                        return (
                          <button key={shift.id}
                            onClick={() => {
                              if (isMe) { showToast('This is your shift. Tap someone else\u2019s to propose a trade.'); return; }
                              if (isPending) {
                                const myPending = allSwapRequests.find(s => ['pending_employee', 'pending_manager'].includes(s.status) && s.requester_id === myEmployee?.id && (s.requester_shift_id === shift.id || s.target_shift_id === shift.id));
                                showToast(myPending ? 'You already have a pending request for this shift.' : 'This shift has a pending swap request.');
                                return;
                              }
                              setSwapTarget({ shift, employee: emp });
                              setSwapMode(null); setSwapError('');
                            }}
                            className={`text-xs px-2.5 py-1.5 rounded-lg border relative ${isMe ? 'bg-blue-50 border-blue-200 text-blue-600 cursor-default' : isPending ? 'bg-amber-50 border-amber-200 text-amber-500 cursor-default' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600'}`}>
                            {shift.shift_name} {new Date(shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                            {shift.role && <span className="text-gray-400 ml-0.5">({shift.role})</span>}
                            {isPending && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1" style={{ verticalAlign: 'middle' }} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* Swap/pickup proposal modal */}
        {swapTarget && (() => {
          const myRoles = myEmployee?.role ? myEmployee.role.split(',').map(r => r.trim()) : [];
          const canPickup = myRoles.includes(swapTarget.shift.role);
          return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 }}>
            <div className="bg-white rounded-t-2xl w-full max-w-sm p-5" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-base font-medium text-gray-900">{swapTarget.employee.name}'s shift</div>
                <button onClick={() => { setSwapTarget(null); setSwapMyShift(null); setSwapNote(''); setSwapMode(null); }} className="text-gray-400 hover:text-gray-600 text-lg">&#x2715;</button>
              </div>

              {/* Target shift info */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${swapTarget.employee.color}`} style={{ fontSize: 9 }}>{swapTarget.employee.initials}</div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{swapTarget.shift.shift_name} on {new Date(swapTarget.shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                    <div className="text-xs text-gray-500">{swapTarget.shift.role} &middot; {formatTime(swapTarget.shift.start_time)}–{formatTime(swapTarget.shift.end_time)}</div>
                  </div>
                </div>
              </div>

              {/* Choose mode if not selected */}
              {!swapMode && (
                <div className="flex flex-col gap-2 mb-3">
                  <button onClick={() => setSwapMode('trade')} className="text-left px-4 py-3.5 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                    <div className="text-sm font-semibold text-gray-900">&#x21C4; Propose a trade</div>
                    <div className="text-xs text-gray-400 mt-0.5">Offer one of your shifts in exchange</div>
                  </button>
                  <button onClick={() => { if (canPickup) setSwapMode('pickup'); }} disabled={!canPickup}
                    className={`text-left px-4 py-3.5 rounded-xl border-2 transition-colors ${canPickup ? 'border-gray-200 hover:border-green-300 hover:bg-green-50' : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'}`}>
                    <div className="text-sm font-semibold text-gray-900">&#x2191; Request this shift</div>
                    <div className="text-xs text-gray-400 mt-0.5">{canPickup ? 'Ask to take this shift without trading' : `You need the ${swapTarget.shift.role} role to request this shift`}</div>
                  </button>
                </div>
              )}

              {/* Trade mode */}
              {swapMode === 'trade' && (
                <>
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-500 mb-2">Select your shift to offer</div>
                    <div className="flex flex-col gap-1.5">
                      {shifts.filter(s => !isShiftPendingSwap(s.id)).map(shift => (
                        <button key={shift.id} onClick={() => setSwapMyShift(shift)}
                          className={`text-left px-3 py-2.5 rounded-lg border text-xs ${swapMyShift?.id === shift.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                          <span className="font-medium">{shift.shift_name}</span> — {new Date(shift.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} ({shift.role})
                        </button>
                      ))}
                      {shifts.filter(s => !isShiftPendingSwap(s.id)).length === 0 && (
                        <div className="text-xs text-gray-400 text-center py-4">No available shifts to swap</div>
                      )}
                    </div>
                  </div>
                  {swapMyShift && swapMyShift.role !== swapTarget.shift.role && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-700">
                      &#x26A0;&#xFE0F; Cross-role swap: you'd work as <strong>{swapTarget.shift.role}</strong>, they'd work as <strong>{swapMyShift.role}</strong>
                    </div>
                  )}
                </>
              )}

              {/* Pickup mode info */}
              {swapMode === 'pickup' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 mb-3 text-xs text-green-700">
                  You're requesting to take this shift. {swapTarget.employee.name} will need to agree to give it up, then the manager will approve.
                </div>
              )}

              {/* Warnings (shown for both modes) */}
              {swapMode && (() => {
                const myRolesArr = myEmployee?.role ? myEmployee.role.split(',').map(r => r.trim()) : [];
                const targetDay = swapTarget.shift.day;
                const doubleShift = shifts.some(s => s.day === targetDay && (swapMode === 'trade' ? s.id !== swapMyShift?.id : true));
                const crossRole = swapMode === 'trade' && swapMyShift && swapMyShift.role !== swapTarget.shift.role;
                const hasTargetRole = myRolesArr.includes(swapTarget.shift.role);
                return <>
                  {doubleShift && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-700">&#x26A0;&#xFE0F; You already work on {new Date(targetDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' })}. This would give you two shifts that day.</div>}
                  {crossRole && !hasTargetRole && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-700">&#x26A0;&#xFE0F; You don't have the {swapTarget.shift.role} role. The manager will need to approve this cross-role swap.</div>}
                  {swapMode === 'pickup' && !hasTargetRole && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-700">&#x26A0;&#xFE0F; You don't have the {swapTarget.shift.role} role. The manager will need to approve this.</div>}
                </>;
              })()}

              {/* Note + submit (shown for both modes) */}
              {swapMode && (
                <>
                  {swapError && <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-600">{swapError}</div>}
                  <input value={swapNote} onChange={e => setSwapNote(e.target.value)} placeholder="Add a note (optional)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-xs mb-3 focus:outline-none focus:border-blue-400 bg-gray-50" />
                  <div className="flex gap-2">
                    <button onClick={() => { setSwapMode(null); setSwapError(''); }} className="flex-1 py-3 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50">Back</button>
                    <button onClick={proposeSwap} disabled={(swapMode === 'trade' && !swapMyShift) || swapSubmitting}
                      className="flex-2 py-3 px-6 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
                      {swapSubmitting ? 'Sending...' : swapMode === 'pickup' ? 'Send request' : 'Send swap request'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          );
        })()}

        {/* Bottom nav */}
        <div className="border-t border-gray-200 px-4 py-3 bg-white">
          <button onClick={onManagerView} className="w-full text-xs text-gray-400 hover:text-gray-600">
            Switch to manager view →
          </button>
        </div>
      </div>

      {/* Time Off Modal */}
      {showTimeOff && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:60}}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm mx-4">
            {timeOffSubmitted ? (
              <div className="text-center py-6">
                <div className="text-3xl mb-2">✓</div>
                <div className="text-sm font-medium text-green-700">Request submitted!</div>
                <div className="text-xs text-green-500 mt-1">Your manager will review it shortly.</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-base font-medium text-gray-900">Request Time Off</div>
                  <button onClick={() => setShowTimeOff(false)} className="text-gray-400 text-lg">✕</button>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setTimeOffMonth(new Date(timeOffMonth.getFullYear(), timeOffMonth.getMonth() - 1, 1))} className="text-sm text-gray-400 hover:text-gray-600 px-2">←</button>
                  <div className="text-sm font-medium text-gray-900">{timeOffMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                  <button onClick={() => setTimeOffMonth(new Date(timeOffMonth.getFullYear(), timeOffMonth.getMonth() + 1, 1))} className="text-sm text-gray-400 hover:text-gray-600 px-2">→</button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['M','T','W','T','F','S','S'].map((d, i) => <div key={i} className="text-center text-xs text-gray-400 py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {getCalendarDays(timeOffMonth).map((d, i) => {
                    if (!d) return <div key={i} />;
                    const ds = d.toISOString().split('T')[0];
                    const sel = timeOffDates.includes(ds);
                    const past = d < new Date(new Date().setHours(0,0,0,0));
                    return (
                      <button key={i} disabled={past} onClick={() => toggleTimeOffDate(ds)}
                        className={`w-full aspect-square rounded-lg text-xs font-medium flex items-center justify-center ${sel ? 'bg-blue-600 text-white' : past ? 'text-gray-200' : 'text-gray-700 hover:bg-gray-100'}`}>
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
                {timeOffDates.length > 0 && (
                  <div className="text-xs text-blue-600 mb-3">{timeOffDates.length} day{timeOffDates.length > 1 ? 's' : ''} selected</div>
                )}
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-400 block mb-1.5">Reason (optional)</label>
                  <textarea value={timeOffNote} onChange={e => setTimeOffNote(e.target.value)} placeholder="e.g. doctor appointment" rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none" />
                </div>
                <button onClick={submitTimeOff} disabled={timeOffDates.length === 0}
                  className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
                  Submit request
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100}}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 max-h-screen overflow-y-auto">

            {/* Progress */}
            <div className="flex gap-1 mb-5">
              {[1,2,3].map(s => (
                <div key={s} className={`flex-1 h-1 rounded-full ${onboardingStep >= s ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
              ))}
            </div>

            {onboardingStep === 1 && (
              <>
                <div className="text-base font-medium text-gray-900 mb-1">Welcome to Posted! 👋</div>
                <div className="text-xs text-gray-400 mb-5">Let's set up your preferences so we can schedule you better.</div>

                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-700 block mb-2">Preferred shift times</label>
                  <div className="grid grid-cols-2 gap-2">
                    {SHIFT_PREFS.map(s => (
                      <button key={s} onClick={() => togglePref(prefShifts, setPrefShifts, s)}
                        className={`py-2 px-3 rounded-xl border text-xs font-medium ${prefShifts.includes(s) ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-5">
                  <label className="text-xs font-medium text-gray-700 block mb-2">Available days</label>
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map(d => (
                      <button key={d} onClick={() => togglePref(prefDays, setPrefDays, d)}
                        className={`py-2 rounded-lg border text-xs font-medium ${prefDays.includes(d) ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-5">
                  <label className="text-xs font-medium text-gray-700 block mb-2">Max hours per week: <span className="text-blue-600">{maxHours} hrs</span></label>
                  <input type="range" min="10" max="50" step="5" value={maxHours}
                    onChange={e => setMaxHours(parseInt(e.target.value))}
                    className="w-full" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>10hrs</span><span>50hrs</span></div>
                </div>

                <button onClick={() => setOnboardingStep(2)}
                  className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-blue-700">
                  Continue →
                </button>
              </>
            )}

            {onboardingStep === 2 && (
              <>
                <div className="text-base font-medium text-gray-900 mb-1">Certifications</div>
                <div className="text-xs text-gray-400 mb-4">Select any certifications you hold.</div>

                <div className="flex flex-col gap-2 mb-5">
                  {CERTS.map(c => (
                    <button key={c} onClick={() => togglePref(certs, setCerts, c)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm text-left ${certs.includes(c) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${certs.includes(c) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                        {certs.includes(c) && <span className="text-white text-xs">✓</span>}
                      </div>
                      {c}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setOnboardingStep(1)}
                    className="flex-1 border border-gray-200 text-gray-500 rounded-xl py-2.5 text-sm hover:bg-gray-50">
                    ← Back
                  </button>
                  <button onClick={() => setOnboardingStep(3)}
                    className="flex-1 bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-blue-700">
                    Continue →
                  </button>
                </div>
              </>
            )}

            {onboardingStep === 3 && (
              <>
                <div className="text-base font-medium text-gray-900 mb-1">Almost done!</div>
                <div className="text-xs text-gray-400 mb-4">Scheduling preferences and final details.</div>

                <div className="text-xs font-medium text-gray-500 mb-2">Shift preferences</div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    { val: canOpen, set: setCanOpen, label: 'Can open', desc: 'I can open the restaurant' },
                    { val: canClose, set: setCanClose, label: 'Can close', desc: 'I can close the restaurant' },
                    { val: prefersDoubles, set: setPrefersDoubles, label: 'Prefer doubles', desc: 'I like working two shifts' },
                    { val: noClopens, set: setNoClopens, label: 'No clopens', desc: "Don't close then open" },
                  ].map(({ val, set, label, desc }) => (
                    <button key={label} onClick={() => set(!val)}
                      className={`text-left px-3 py-2.5 rounded-xl border text-xs ${val ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className={`font-medium ${val ? 'text-blue-700' : 'text-gray-500'}`}>{val ? '\u2713 ' : ''}{label}</div>
                      <div className="text-gray-400 mt-0.5" style={{ fontSize: 10 }}>{desc}</div>
                    </button>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-700 block mb-1.5">Emergency contact</label>
                  <input type="text" value={emergencyContact} onChange={e => setEmergencyContact(e.target.value)}
                    placeholder="Name & phone number"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                </div>

                <div className="mb-5">
                  <label className="text-xs font-medium text-gray-700 block mb-1.5">Anything else your manager should know?</label>
                  <textarea value={employeeNotes} onChange={e => setEmployeeNotes(e.target.value)}
                    placeholder="e.g. can't lift heavy items, allergies, transportation limitations..."
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none" />
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setOnboardingStep(2)}
                    className="flex-1 border border-gray-200 text-gray-500 rounded-xl py-2.5 text-sm hover:bg-gray-50">
                    ← Back
                  </button>
                  <button onClick={saveOnboarding}
                    className="flex-1 bg-green-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-green-700">
                    Complete profile ✓
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Release Shift Modal */}
      {showReleaseModal && releaseShift && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-medium text-gray-900">Release shift</div>
              <button onClick={() => setShowReleaseModal(false)} className="text-gray-400 text-lg">✕</button>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <div className="text-sm font-medium text-gray-800">{formatShiftDate(releaseShift.day)}</div>
              <div className="text-xs text-gray-500 mt-0.5">{releaseShift.shift_name} · {releaseShift.role}</div>
            </div>

            {releaseStep === 1 && (
              <>
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Reason (optional)</label>
                  <textarea value={releaseReason} onChange={e => setReleaseReason(e.target.value)}
                    placeholder="e.g. family emergency, appointment..." rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none" />
                </div>
                <div className="mb-5">
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">Offer incentive (optional)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">$</span>
                    <input type="number" value={releaseIncentive} onChange={e => setReleaseIncentive(e.target.value)}
                      placeholder="0"
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Cash paid directly — not through the app</div>
                </div>
                <button onClick={goToStep2}
                  className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-blue-700">
                  See who's available ✦ AI
                </button>
              </>
            )}

            {releaseStep === 2 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-xs font-medium text-gray-700">AI-ranked candidates</div>
                  {rankingLoading && <div className="text-xs text-purple-600 animate-pulse">✦ Analyzing...</div>}
                </div>

                {availableCoworkers.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-center">
                    <div className="text-sm text-amber-700 font-medium">No one available</div>
                    <div className="text-xs text-amber-500 mt-1">Manager will be notified to find coverage</div>
                  </div>
                ) : rankingLoading ? (
                  <div className="flex flex-col gap-2 mb-4">
                    {availableCoworkers.map((cw, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl animate-pulse">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${cw.color}`}>{cw.initials}</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{cw.name}</div>
                          <div className="text-xs text-gray-400">Analyzing...</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 mb-4">
                    {(aiRankings.length > 0 ? aiRankings : availableCoworkers.map(cw => ({ id: cw.id, name: cw.name, reason: 'Available this day', tag: 'Available' }))).map((ranked, i) => {
                      const cw = employees.find(e => e.id === ranked.id);
                      if (!cw) return null;
                      const tagColor = ranked.tag === 'Best match' ? 'bg-green-100 text-green-700' : ranked.tag === 'Good option' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500';
                      return (
                        <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${i === 0 && aiRankings.length > 0 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${cw.color}`}>{cw.initials}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <div className="text-sm font-medium text-gray-900">{cw.name}</div>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tagColor}`}>{ranked.tag}</span>
                            </div>
                            <div className="text-xs text-gray-500">{ranked.reason}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {releaseIncentive > 0 && (
                  <div className="bg-green-50 border border-green-100 rounded-xl p-3 mb-4 text-xs text-green-700">
                    💰 Offering <strong>${releaseIncentive}</strong> to whoever takes this shift
                  </div>
                )}

                <button onClick={releaseShiftSubmit}
                  className="w-full bg-red-500 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-red-600">
                  Release shift & notify manager
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}